import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import ScrollToTop from './common/ScrollToTop';

// Lazy load pages for performance optimization
const HomePage = lazy(() => import('./pages/HomePage'));
const BiblePassageOfTheDay = lazy(() => import('./pages/BiblePassageOfTheDay'));
const Corinthians13 = lazy(() => import('./pages/Corinthians13'));
const ScriptureVerses = lazy(() => import('./pages/ScriptureVerses'));
const BibleVerses = lazy(() => import('./pages/BibleVerses'));
const BibleVerseOfTheDay = lazy(() => import('./pages/BibleVerseOfTheDay'));
const VerseOfTheDay = lazy(() => import('./pages/VerseOfTheDay'));
const EncouragingBibleVerses = lazy(() => import('./pages/EncouragingBibleVerses'));
const EncouragingVerses = lazy(() => import('./pages/EncouragingVerses'));
const DailyBibleVerse = lazy(() => import('./pages/DailyBibleVerse'));
const HealingScriptures = lazy(() => import('./pages/HealingScriptures'));
const BibleVersesAboutStrength = lazy(() => import('./pages/BibleVersesAboutStrength'));
const Jeremiah2911 = lazy(() => import('./pages/Jeremiah2911'));
const BibleVersesAboutLove = lazy(() => import('./pages/BibleVersesAboutLove'));
const DailyScripture = lazy(() => import('./pages/DailyScripture'));
const Romans828 = lazy(() => import('./pages/Romans828'));
const Isaiah4110 = lazy(() => import('./pages/Isaiah4110'));
const HealingScripturesInTheBible = lazy(() => import('./pages/HealingScripturesInTheBible'));
const BibleQuoteOfTheDay = lazy(() => import('./pages/BibleQuoteOfTheDay'));
const BibleScriptureOfTheDay = lazy(() => import('./pages/BibleScriptureOfTheDay'));
const BibleScripturesForStrength = lazy(() => import('./pages/BibleScripturesForStrength'));
const BibleVersesAboutWisdom = lazy(() => import('./pages/BibleVersesAboutWisdom'));
const Proverbs35 = lazy(() => import('./pages/Proverbs35'));
const BibleVersesAboutHope = lazy(() => import('./pages/BibleVersesAboutHope'));
const BibleVersesAboutFaith = lazy(() => import('./pages/BibleVersesAboutFaith'));
const BibleVersesAboutPeace = lazy(() => import('./pages/BibleVersesAboutPeace'));
const Proverbs31Woman = lazy(() => import('./pages/Proverbs31Woman'));
const ChristianGifts = lazy(() => import('./pages/ChristianGifts'));
const Psalm23 = lazy(() => import('./pages/Psalm23'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
  </div>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/bible-passage-of-the-day" element={<BiblePassageOfTheDay />} />
              <Route path="/1-corinthians-13" element={<Corinthians13 />} />
              <Route path="/scripture-verses" element={<ScriptureVerses />} />
              <Route path="/bible-verses" element={<BibleVerses />} />
              <Route path="/bible-verse-of-the-day" element={<BibleVerseOfTheDay />} />
              <Route path="/verse-of-the-day" element={<VerseOfTheDay />} />
              <Route path="/encouraging-bible-verses" element={<EncouragingBibleVerses />} />
              <Route path="/encouraging-verses" element={<EncouragingVerses />} />
              <Route path="/daily-bible-verse" element={<DailyBibleVerse />} />
              <Route path="/healing-scriptures" element={<HealingScriptures />} />
              <Route path="/bible-verses-about-strength" element={<BibleVersesAboutStrength />} />
              <Route path="/jeremiah-29-11" element={<Jeremiah2911 />} />
              <Route path="/bible-verses-about-love" element={<BibleVersesAboutLove />} />
              <Route path="/daily-scripture" element={<DailyScripture />} />
              <Route path="/romans-8-28" element={<Romans828 />} />
              <Route path="/isaiah-41-10" element={<Isaiah4110 />} />
              <Route path="/healing-scriptures-in-the-bible" element={<HealingScripturesInTheBible />} />
              <Route path="/bible-quote-of-the-day" element={<BibleQuoteOfTheDay />} />
              <Route path="/bible-scripture-of-the-day" element={<BibleScriptureOfTheDay />} />
              <Route path="/bible-scriptures-for-strength" element={<BibleScripturesForStrength />} />
              <Route path="/bible-verses-about-wisdom" element={<BibleVersesAboutWisdom />} />
              <Route path="/proverbs-3-5" element={<Proverbs35 />} />
              <Route path="/bible-verses-about-hope" element={<BibleVersesAboutHope />} />
              <Route path="/bible-verses-about-faith" element={<BibleVersesAboutFaith />} />
              <Route path="/bible-verses-about-peace" element={<BibleVersesAboutPeace />} />
              <Route path="/proverbs-31-woman" element={<Proverbs31Woman />} />
              <Route path="/christian-gifts" element={<ChristianGifts />} />
              <Route path="/psalm-23" element={<Psalm23 />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
            </Routes>
          </Suspense>
        </motion.main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;